import {
  Box,
  Button,
  ProgressBar,
  SxProps,
  Typography,
} from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import React from "react";
import { useAnalytics } from "use-analytics";

import { QuizQuestion } from "./types";

export function QuestionCard({
  topic,
  question,
  questionIndex,
  totalQuestions,
  correctSoFar,
  selectedAnswer,
  showFeedback,
  onSelectAnswer,
  onNextQuestion,
}: {
  topic: string;
  question: QuizQuestion;
  questionIndex: number;
  totalQuestions: number;
  correctSoFar: number;
  selectedAnswer: number | undefined;
  showFeedback: boolean;
  onSelectAnswer: (questionIndex: number, optionIndex: number) => void;
  onNextQuestion: () => void;
}) {
  const { t } = useTranslation("learnerApp\\Quiz");
  const { track } = useAnalytics();
  const answeredCount = questionIndex + (selectedAnswer !== undefined ? 1 : 0);
  const progressPercent =
    totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
  const answeredCorrect =
    showFeedback &&
    selectedAnswer !== undefined &&
    selectedAnswer === question.answerIndex;

  const handleSelectOption = (optionIndex: number) => {
    if (showFeedback) {
      return;
    }
    track("quiz_question_answer_select", {
      topic: topic.trim(),
      question_index: questionIndex,
    });
    onSelectAnswer(questionIndex, optionIndex);
  };

  return (
    <Box
      sx={{
        backgroundColor: "common.white",
        borderRadius: "20px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        border: "1px solid",
        borderColor: "grey.100",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: { xs: "20px", sm: "28px" },
          paddingBottom: { xs: "16px", sm: "20px" },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "text.primary", marginBottom: "4px" }}
        >
          {t("Quiz Writer")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            marginTop: "4px",
            fontWeight: 500,
            fontSize: "1.5rem",
          }}
        >
          {topic.trim() || t("Quiz")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "20px",
            gap: "16px",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "grey.700", fontSize: "1.3rem" }}
          >
            {t("Question {{current}} of {{total}}", {
              current: questionIndex + 1,
              total: totalQuestions,
            })}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "1.3rem" }}
          >
            {t("{{count}} correct so far", { count: correctSoFar })}
          </Typography>
        </Box>
        <ProgressBar
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 8,
            borderRadius: 999,
            marginTop: "12px",
            backgroundColor: "#DCFCE7",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              backgroundColor: "#6EE7B7",
            },
          }}
        />
      </Box>
      <Box sx={{ height: "1px", backgroundColor: "grey.200", opacity: 0.7 }} />
      <Box
        sx={{
          padding: { xs: "20px", sm: "28px" },
          paddingTop: { xs: "20px", sm: "24px" },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            marginBottom: "24px",
            fontSize: "1.6rem",
            lineHeight: 1.4,
          }}
        >
          {question.question}
        </Typography>
        <Box flex sx={{ flexDirection: "column", gap: "14px" }}>
          {question.options.map((option, optionIndex) => {
            const isSelected = selectedAnswer === optionIndex;
            const isCorrectOption = optionIndex === question.answerIndex;
            const fadedOthers = showFeedback && !isCorrectOption && !isSelected;

            return (
              <Box
                key={optionIndex}
                role="button"
                tabIndex={showFeedback ? -1 : 0}
                onClick={() => handleSelectOption(optionIndex)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectOption(optionIndex);
                  }
                }}
                sx={getQuizOptionSx(showFeedback, isSelected, isCorrectOption)}
              >
                <OptionStatusIcon
                  showFeedback={showFeedback}
                  isSelected={isSelected}
                  isCorrectOption={isCorrectOption}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: fadedOthers ? 400 : 500,
                    color: fadedOthers ? "grey.500" : "text.primary",
                    textAlign: "left",
                    fontSize: "1.5rem",
                  }}
                >
                  {option}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {showFeedback && selectedAnswer !== undefined && (
          <Box sx={{ marginTop: "24px" }}>
            {answeredCorrect ? (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: "success.main",
                  marginBottom: question.explanation ? "12px" : 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "1.5rem",
                }}
              >
                <span aria-hidden>⭐</span> {t("Nice one!")}
              </Typography>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: "error.main",
                  marginBottom: question.explanation ? "12px" : 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "1.5rem",
                }}
              >
                <span aria-hidden>💡</span>{" "}
                {t("Not quite, but you are learning!")}
              </Typography>
            )}
            {question.explanation ? (
              <Box
                sx={{
                  padding: "16px",
                  borderRadius: "14px",
                  backgroundColor: "grey.100",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "grey.700",
                    lineHeight: 1.55,
                    fontSize: "1.4rem",
                  }}
                >
                  {question.explanation}
                </Typography>
              </Box>
            ) : null}
          </Box>
        )}

        {showFeedback && selectedAnswer !== undefined && (
          <Button
            onClick={() => {
              track("quiz_question_next_click", {
                topic: topic.trim(),
                question_index: questionIndex,
              });
              onNextQuestion();
            }}
            variant="contained"
            size="large"
            sx={{
              borderRadius: "999px",
              padding: "12px 24px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.5rem",
              alignSelf: "flex-start",
              marginTop: "24px",
              backgroundColor: "#7C3AED",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#6D28D9",
                boxShadow: "none",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {questionIndex < totalQuestions - 1
                ? t("Next question")
                : t("See results")}
              <Box component="span" sx={{ fontSize: "1.5rem", lineHeight: 1 }}>
                →
              </Box>
            </Box>
          </Button>
        )}
      </Box>
    </Box>
  );
}

function OptionStatusIcon({
  showFeedback,
  isSelected,
  isCorrectOption,
}: {
  showFeedback: boolean;
  isSelected: boolean;
  isCorrectOption: boolean;
}) {
  if (!showFeedback) {
    return null;
  }

  if (isCorrectOption) {
    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: QUIZ_CORRECT_REVEAL_BORDER,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "common.white",
          fontSize: "1.5rem",
          fontWeight: 700,
        }}
        aria-hidden
      >
        ✓
      </Box>
    );
  }

  if (isSelected) {
    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: "error.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "common.white",
          fontSize: "1.5rem",
          lineHeight: 1,
          fontWeight: 700,
        }}
        aria-hidden
      >
        ×
      </Box>
    );
  }

  return <Box sx={{ width: 28, flexShrink: 0 }} aria-hidden />;
}

/** User's chosen option uses a blue border; revealed correct answer the learner missed uses a green border (see quiz mocks). */
const QUIZ_USER_CHOICE_BORDER = "#2563EB";
const QUIZ_CORRECT_REVEAL_BORDER = "#22C55E";

function getQuizOptionSx(
  showFeedback: boolean,
  isSelected: boolean,
  isCorrectOption: boolean,
): SxProps {
  const base: SxProps = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px 20px",
    borderRadius: "14px",
    transition:
      "background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease",
    cursor: showFeedback ? "default" : "pointer",
  };

  if (!showFeedback) {
    return {
      ...base,
      border: "1px solid",
      borderColor: isSelected ? QUIZ_USER_CHOICE_BORDER : "grey.300",
      backgroundColor: "common.white",
      boxShadow: isSelected ? "0 0 0 1px rgba(37, 99, 235, 0.15)" : "none",
      "&:hover": {
        borderColor: isSelected ? QUIZ_USER_CHOICE_BORDER : "grey.400",
      },
    };
  }

  if (isCorrectOption && isSelected) {
    return {
      ...base,
      border: "2px solid",
      borderColor: QUIZ_CORRECT_REVEAL_BORDER,
      backgroundColor: "#ECFDF5",
    };
  }

  if (isCorrectOption && !isSelected) {
    return {
      ...base,
      border: "2px solid",
      borderColor: QUIZ_CORRECT_REVEAL_BORDER,
      backgroundColor: "#F0FDF4",
    };
  }

  if (!isCorrectOption && isSelected) {
    return {
      ...base,
      border: "2px solid",
      borderColor: "#EF4444",
      backgroundColor: "#FEF2F2",
    };
  }

  return {
    ...base,
    border: "1px solid",
    borderColor: "grey.200",
    backgroundColor: "grey.50",
    opacity: 0.55,
  };
}
